/**
 * Wild Faaji Events Module
 * 
 * Provides client-side functionality for the events section including:
 * - Event filtering and sorting logic
 * - Countdown initialization for upcoming events
 * - Ticket link tracking
 * 
 * Requirements: 5.1, 5.3
 */

/**
 * EventsManager class
 * Manages events section functionality
 */
class EventsManager {
  /**
   * Create an events manager
   * @param {Object} options - Configuration options
   * @param {HTMLElement} options.element - The events section element
   * @param {Function} [options.onTicketClick] - Callback when ticket link is clicked
   */
  constructor(options) {
    if (!options.element) {
      throw new Error('EventsManager requires an element');
    }

    this.element = options.element;
    this.onTicketClick = options.onTicketClick || null;
    this.eventCards = [];
    this.countdownTimers = [];

    this.init();
  }

  /**
   * Initialize the events manager
   */
  init() {
    // Find all event cards
    this.eventCards = Array.from(
      this.element.querySelectorAll('.wf-event-card')
    );

    // Initialize countdowns for upcoming events
    this.initCountdowns();

    // Set up ticket link tracking
    this.setupTicketTracking();

    // Set up filtering if needed
    this.setupFiltering();

    // Sort events by date
    this.sortEvents();
  }

  /**
   * Initialize countdown timers for upcoming events
   */
  initCountdowns() {
    // Find all countdown elements within the events section
    const countdownElements = this.element.querySelectorAll('[data-countdown]');

    countdownElements.forEach((element) => {
      try {
        const endDate = element.getAttribute('data-end-date');

        if (!endDate) {
          console.warn('Countdown element missing data-end-date attribute', element);
          return;
        }

        // Check if timer already initialized
        if (element.hasAttribute('data-countdown-initialized')) {
          return;
        }

        // Mark as initialized
        element.setAttribute('data-countdown-initialized', 'true');

        // Create timer instance using the global CountdownTimer class
        if (typeof window.WildFaaji !== 'undefined' && window.WildFaaji.CountdownTimer) {
          const timer = new window.WildFaaji.CountdownTimer({
            element,
            endDate,
            onComplete: () => {
              // When countdown completes, update the event card status
              const eventCard = element.closest('.wf-event-card');
              if (eventCard) {
                eventCard.classList.remove('wf-event-card--upcoming');
                eventCard.classList.add('wf-event-card--live');
                
                // Add live badge if not present
                this.addLiveBadge(eventCard);
              }
            },
          });

          this.countdownTimers.push(timer);
        } else {
          console.warn('CountdownTimer class not available');
        }
      } catch (error) {
        console.error('Error initializing countdown timer:', error, element);
      }
    });
  }

  /**
   * Add live badge to event card
   * @param {HTMLElement} eventCard - The event card element
   */
  addLiveBadge(eventCard) {
    const imageWrapper = eventCard.querySelector('.wf-event-card__image-wrapper');
    
    if (imageWrapper && !imageWrapper.querySelector('.wf-event-card__live-badge')) {
      const liveBadge = document.createElement('div');
      liveBadge.className = 'wf-event-card__live-badge';
      liveBadge.innerHTML = '<span class="wf-event-card__live-text">LIVE NOW</span>';
      imageWrapper.appendChild(liveBadge);
    }
  }

  /**
   * Set up ticket link tracking
   */
  setupTicketTracking() {
    const ticketLinks = this.element.querySelectorAll('.wf-event-card__ticket-button');

    ticketLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        const eventCard = link.closest('.wf-event-card');
        const eventId = eventCard ? eventCard.getAttribute('data-event-id') : null;
        const eventTitle = eventCard ? eventCard.querySelector('.wf-event-card__title')?.textContent : null;
        const ticketUrl = link.getAttribute('href');

        // Track the click
        this.trackTicketClick({
          eventId,
          eventTitle,
          ticketUrl,
        });

        // Call custom callback if provided
        if (this.onTicketClick && typeof this.onTicketClick === 'function') {
          this.onTicketClick({
            eventId,
            eventTitle,
            ticketUrl,
            event,
          });
        }
      });
    });
  }

  /**
   * Track ticket link click
   * @param {Object} data - Tracking data
   * @param {string} data.eventId - Event ID
   * @param {string} data.eventTitle - Event title
   * @param {string} data.ticketUrl - Ticket URL
   */
  trackTicketClick(data) {
    // Log to console in development
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1')) {
      console.log('Ticket link clicked:', data);
    }

    // Dispatch custom event for analytics integration
    document.dispatchEvent(
      new CustomEvent('wf:ticket-click', {
        detail: data,
      })
    );

    // If Google Analytics is available, track the event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'ticket_click', {
        event_id: data.eventId,
        event_title: data.eventTitle,
        ticket_url: data.ticketUrl,
      });
    }

    // If Facebook Pixel is available, track the event
    if (typeof fbq !== 'undefined') {
      fbq('track', 'ViewContent', {
        content_name: data.eventTitle,
        content_category: 'Event',
        content_ids: [data.eventId],
      });
    }
  }

  /**
   * Set up event filtering
   */
  setupFiltering() {
    // Check if there's a filter control in the section
    const filterControl = this.element.querySelector('[data-events-filter]');

    if (!filterControl) {
      return;
    }

    filterControl.addEventListener('change', (event) => {
      const filterValue = event.target.value;
      this.filterEvents(filterValue);
    });
  }

  /**
   * Filter events by status
   * @param {string} status - Filter status ('all', 'upcoming', 'past', 'live')
   */
  filterEvents(status) {
    this.eventCards.forEach((card) => {
      const cardStatus = card.classList.contains('wf-event-card--upcoming')
        ? 'upcoming'
        : card.classList.contains('wf-event-card--live')
        ? 'live'
        : card.classList.contains('wf-event-card--past')
        ? 'past'
        : 'unknown';

      if (status === 'all' || cardStatus === status) {
        card.style.display = '';
        card.setAttribute('aria-hidden', 'false');
      } else {
        card.style.display = 'none';
        card.setAttribute('aria-hidden', 'true');
      }
    });

    // Dispatch filter event
    this.element.dispatchEvent(
      new CustomEvent('wf:events-filtered', {
        bubbles: true,
        detail: { status },
      })
    );
  }

  /**
   * Sort events by date
   */
  sortEvents() {
    // Get the grid container
    const grid = this.element.querySelector('.wf-events-grid');

    if (!grid || this.eventCards.length === 0) {
      return;
    }

    // Sort event cards by date attribute
    const sortedCards = this.eventCards.sort((a, b) => {
      const dateA = this.getEventDate(a);
      const dateB = this.getEventDate(b);

      if (!dateA || !dateB) {
        return 0;
      }

      return dateA - dateB;
    });

    // Re-append cards in sorted order
    sortedCards.forEach((card) => {
      grid.appendChild(card);
    });
  }

  /**
   * Get event date from card
   * @param {HTMLElement} card - Event card element
   * @returns {Date|null} Event date
   */
  getEventDate(card) {
    const timeElement = card.querySelector('time[datetime]');

    if (!timeElement) {
      return null;
    }

    const datetime = timeElement.getAttribute('datetime');
    const date = new Date(datetime);

    return isNaN(date.getTime()) ? null : date;
  }

  /**
   * Refresh countdowns
   */
  refreshCountdowns() {
    // Stop existing timers
    this.countdownTimers.forEach((timer) => {
      if (timer && typeof timer.destroy === 'function') {
        timer.destroy();
      }
    });

    this.countdownTimers = [];

    // Reinitialize countdowns
    this.initCountdowns();
  }

  /**
   * Destroy the events manager
   */
  destroy() {
    // Stop all countdown timers
    this.countdownTimers.forEach((timer) => {
      if (timer && typeof timer.destroy === 'function') {
        timer.destroy();
      }
    });

    this.countdownTimers = [];
    this.eventCards = [];
    this.element = null;
    this.onTicketClick = null;
  }
}

/**
 * Initialize all events sections on the page
 * @param {string} [selector='.wf-events-section'] - CSS selector for events sections
 * @returns {Array<EventsManager>} Array of initialized events managers
 */
function initEvents(selector = '.wf-events-section') {
  const eventsSections = document.querySelectorAll(selector);
  const managers = [];

  eventsSections.forEach((element) => {
    try {
      // Check if manager already initialized
      if (element.hasAttribute('data-events-initialized')) {
        return;
      }

      // Mark as initialized
      element.setAttribute('data-events-initialized', 'true');

      // Create manager instance
      const manager = new EventsManager({
        element,
      });

      managers.push(manager);
    } catch (error) {
      console.error('Error initializing events manager:', error, element);
    }
  });

  return managers;
}

/**
 * Initialize events when DOM is ready
 */
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initEvents();
    });
  } else {
    // DOM already loaded
    initEvents();
  }
}

// Export for use in other modules and tests
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { EventsManager, initEvents };
}

// Also expose globally for Shopify theme usage
if (typeof window !== 'undefined') {
  window.WildFaaji = window.WildFaaji || {};
  window.WildFaaji.EventsManager = EventsManager;
  window.WildFaaji.initEvents = initEvents;
}
